import React, { useEffect, useState } from 'react';
import { X, Type, AlignLeft, Shapes } from 'lucide-react';
import FilterableMultiSelect from '../../common/FilterableMultiSelect';
import { ingestionService } from '../../../../components/api/ingestionService';
import { apiService } from '../../../../components/api/apiService';

type Props = {
    onClose: () => void;
    onSubmit: (data: any) => void;
    tenantId: string;
    userId: string;
};

const KnowledgeEntityModal: React.FC<Props> = ({ onClose, onSubmit, tenantId, userId }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
    const [entityOptions, setEntityOptions] = useState<{ label: string; value: string }[]>([]);

    useEffect(() => {
        const loadEntities = async () => {
            try {
                const response = await ingestionService.loadDataSources(tenantId);
                const options = response.map((ds: any) => ({
                    label: ds.connectionName,
                    value: ds.id
                }));
                setEntityOptions(options);
            } catch (err) {
                console.error('Failed to load data sources', err);
            }
        };
        loadEntities();
    }, [tenantId]);

    const handleSubmit = async () => {
        if (!title.trim()) return alert("Title is required.");

        const payload = {
            tenantId,
            title: title.trim(),
            description: description.trim(),
            createdByUserId: userId,
            entities: selectedEntities
        };

        try {
            await apiService.post('/api/knowledgebase/createkbentity', payload, 'backend');
            onSubmit(payload);
            onClose();
        } catch (err) {
            console.error('Error submitting entity:', err);
            alert('Failed to create knowledge entity.');
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-neutral-900 border border-neutral-700 text-white rounded-xl shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                        <Shapes className="w-4 h-4 text-indigo-400" />
                        Create Knowledge Workspace
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-4 space-y-4 text-sm">
                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <Type className="w-4 h-4 text-indigo-500" />
                            Title
                        </label>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            placeholder="e.g. Finance Knowledge Group"
                        />
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <AlignLeft className="w-4 h-4 text-indigo-500" />
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            placeholder="Short description about this entity group"
                        />
                    </div>

                    <div>
                        <FilterableMultiSelect
                            label="Entities"
                            options={entityOptions}
                            selected={selectedEntities}
                            setSelected={setSelectedEntities}
                        />
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-neutral-800 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="text-sm px-4 py-1.5 rounded bg-neutral-700 hover:bg-neutral-600 transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="text-sm px-4 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white transition"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default KnowledgeEntityModal;
